import { 
  collection, 
  addDoc, 
  getDocs, 
  getDoc,
  doc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  setDoc,
  runTransaction
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { AvatarItem, UserAvatar, UserInventory, PersonalRecord } from '../types';
import { ReadyPlayerMeAssetService } from './readyPlayerMeAssetService';
import { Capacitor } from '@capacitor/core';
import { firestoreRestApi } from '../utils/firestoreRestApi';
import { formatPRTimeDisplay, normalizeMinutesForStorage } from '../utils/prTimeFormat';

export const avatarService = {
  // Avatar Items Management (Coach functions)
  
  // Create a new avatar item for the shop
  async createAvatarItem(
    teamId: string,
    coachId: string,
    itemData: Omit<AvatarItem, 'id' | 'teamId' | 'coachId' | 'createdAt' | 'updatedAt'>,
    createRpmAsset: boolean = false
  ): Promise<{ itemId: string; rpmAssetId?: string; error?: string }> {
    try {
      let rpmAssetId: string | undefined;
      
      // Create ReadyPlayerMe asset if requested
      if (createRpmAsset) {
        try {
          const rpmResult = await ReadyPlayerMeAssetService.createAsset({
            name: itemData.name,
            category: itemData.category,
            locked: true, // Start locked, unlock when purchased
            visibility: 'private'
          });
          
          if (rpmResult.success && rpmResult.assetId) {
            rpmAssetId = rpmResult.assetId;
          } else {
            console.warn('Failed to create RPM asset, continuing with local item only:', rpmResult.error);
          }
        } catch (error) {
          console.warn('RPM asset creation failed, continuing with local item only:', error);
        }
      }
      
      const itemsCollection = collection(db, 'avatarItems');
      
      const newItem: Omit<AvatarItem, 'id'> = {
        ...itemData,
        teamId,
        coachId,
        isActive: true,
        rpmAssetId,
        rpmLocked: true, // Default to locked
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      const docRef = await addDoc(itemsCollection, newItem);
      
      return {
        itemId: docRef.id,
        rpmAssetId,
        error: rpmAssetId ? undefined : 'RPM asset creation failed, but local item created successfully'
      };
    } catch (error) {
      console.error('Failed to create avatar item:', error);
      throw new Error('Failed to create avatar item. Please try again.');
    }
  },

  // Get all available items for a team's shop
  async getAvailableItems(teamId: string): Promise<AvatarItem[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'avatarItems',
          [
            { field: 'teamId', op: '==', value: teamId },
            { field: 'isActive', op: '==', value: true }
          ]
        );
        
        const items = documents.map(doc => {
          const docData = doc.data();
          return {
            id: doc.id,
            ...docData,
            createdAt: docData.createdAt || Timestamp.now(),
            updatedAt: docData.updatedAt || Timestamp.now()
          } as AvatarItem;
        });
        
        return items.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime; // Descending order
        });
      }
      
      // On web/Android, use SDK
      // Simple query without orderBy to avoid composite index requirement
      try {
        const q = query(
          collection(db, 'avatarItems'),
          where('teamId', '==', teamId),
          where('isActive', '==', true)
        );
        
        const querySnapshot = await getDocs(q);
        
        // Sort in JavaScript instead of Firestore
        const items = querySnapshot.docs.map(doc => {
          const docData = doc.data();
          return {
            id: doc.id,
            ...docData,
            createdAt: docData.createdAt,
            updatedAt: docData.updatedAt
          } as AvatarItem;
        });
        
        return items.sort((a, b) => {
          const aTime = a.createdAt?.seconds || 0;
          const bTime = b.createdAt?.seconds || 0;
          return bTime - aTime; // Descending order
        });
      } catch (sdkError: any) {
        if (sdkError?.code === 'permission-denied' || sdkError?.message?.includes('permission')) {
          console.warn('Permission denied via SDK; retrying getAvailableItems via REST API');
          const documents = await firestoreRestApi.queryDocuments(
            'avatarItems',
            [
              { field: 'teamId', op: '==', value: teamId },
              { field: 'isActive', op: '==', value: true }
            ]
          );
          
          const items = documents.map(doc => {
            const docData = doc.data();
            return {
              id: doc.id,
              ...docData,
              createdAt: docData.createdAt || Timestamp.now(),
              updatedAt: docData.updatedAt || Timestamp.now()
            } as AvatarItem;
          });
          
          return items.sort((a, b) => {
            const aTime = a.createdAt?.seconds || 0;
            const bTime = b.createdAt?.seconds || 0;
            return bTime - aTime;
          });
        }
        throw sdkError;
      }
    } catch (error) {
      console.warn('Failed to load available items:', error);
      return []; // Return empty array if collection doesn't exist or permission denied
    }
  },

  // Get all items for a team (including inactive ones) - for coaches
  async getAllItems(teamId: string): Promise<AvatarItem[]> {
    try {
      const q = query(
        collection(db, 'avatarItems'),
        where('teamId', '==', teamId)
      );
      
      const querySnapshot = await getDocs(q);
      
      const items = querySnapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
          createdAt: docData.createdAt,
          updatedAt: docData.updatedAt
        } as AvatarItem;
      });
      
      return items.sort((a, b) => {
        const aTime = a.createdAt?.seconds || 0;
        const bTime = b.createdAt?.seconds || 0;
        return bTime - aTime; // Descending order
      });
    } catch (error) {
      console.warn('Failed to load all items:', error);
      return [];
    }
  },

  // Update an avatar item
  async updateAvatarItem(itemId: string, updateData: Partial<AvatarItem>): Promise<void> {
    const itemRef = doc(db, 'avatarItems', itemId);
    
    const cleanedUpdateData = {
      ...updateData,
      updatedAt: Timestamp.now()
    };
    
    // Filter out undefined values
    const finalUpdateData = Object.fromEntries(
      Object.entries(cleanedUpdateData).filter(([_, value]) => value !== undefined)
    );
    
    await updateDoc(itemRef, finalUpdateData);
  },

  // Delete an avatar item
  async deleteAvatarItem(itemId: string): Promise<void> {
    const itemRef = doc(db, 'avatarItems', itemId);
    await deleteDoc(itemRef);
  },



  // User Avatar Management

  // Get user's avatar configuration
  async getUserAvatar(userId: string, teamId: string): Promise<UserAvatar | null> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const avatarDoc = await firestoreRestApi.getDocument('userAvatars', `${teamId}_${userId}`);
        if (!avatarDoc) return null;
        
        const docData = avatarDoc.data();
        return {
          id: avatarDoc.id,
          ...docData,
          createdAt: docData.createdAt || Timestamp.now(),
          updatedAt: docData.updatedAt || Timestamp.now()
        } as UserAvatar;
      }
      
      // On web/Android, use SDK
      const avatarRef = doc(db, 'userAvatars', `${teamId}_${userId}`);
      const avatarDoc = await getDoc(avatarRef);
      
      if (!avatarDoc.exists()) {
        return null;
      }
      
      const docData = avatarDoc.data();
      return {
        id: avatarDoc.id,
        ...docData,
        createdAt: docData.createdAt,
        updatedAt: docData.updatedAt
      } as UserAvatar;
    } catch (error) {
      console.warn('Failed to load user avatar:', error);
      return null; // Return null if collection doesn't exist or permission denied
    }
  },

  // Check for avatar updates from ReadyPlayerMe
  async checkForAvatarUpdates(userId: string, teamId: string): Promise<UserAvatar | null> {
    try {
      console.log('Checking for ReadyPlayerMe avatar updates for user:', userId);
      
      // Get current avatar data
      const currentAvatar = await this.getUserAvatar(userId, teamId);
      if (!currentAvatar) {
        console.log('No current avatar found');
        return null;
      }

      console.log('Current avatar ID:', currentAvatar.readyPlayerMeAvatarId);
      
      // For now, we'll return the current avatar
      // In the future, we could implement a ReadyPlayerMe API call to check for updates
      // But since ReadyPlayerMe doesn't provide a direct API to get user's latest avatar,
      // we'll rely on the callback mechanism
      
      return currentAvatar;
    } catch (error) {
      console.error('Failed to check for avatar updates:', error);
      return null;
    }
  },

  // Create or update user avatar
  async saveUserAvatar(
    userId: string, 
    teamId: string, 
    avatarData: Partial<UserAvatar>
  ): Promise<UserAvatar> {
    const avatarRef = doc(db, 'userAvatars', `${teamId}_${userId}`);
    
    const avatarDoc = await getDoc(avatarRef);
    
    if (avatarDoc.exists()) {
      // Update existing avatar
      const updateData = {
        ...avatarData,
        updatedAt: Timestamp.now()
      };
      
      // Filter out undefined values before updating Firestore
      const cleanedUpdateData = Object.fromEntries(
        Object.entries(updateData).filter(([_, value]) => value !== undefined)
      );
      
      await updateDoc(avatarRef, cleanedUpdateData);
      
      // Return the updated avatar
      const updatedDoc = await getDoc(avatarRef);
      const docData = updatedDoc.data();
      if (!docData) {
        throw new Error('Failed to retrieve updated avatar data');
      }
      return {
        id: updatedDoc.id,
        ...docData,
        createdAt: docData.createdAt,
        updatedAt: docData.updatedAt
      } as UserAvatar;
    } else {
      // Create new avatar
      const newAvatar: Omit<UserAvatar, 'id'> = {
        userId,
        teamId,
        skinTone: '#FDBCB4', // Default skin tone
        hairColor: '#8B4513', // Default hair color
        eyeColor: '#000000', // Default eye color
        ...avatarData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };
      
      // Filter out undefined values before saving to Firestore
      const cleanedAvatar = Object.fromEntries(
        Object.entries(newAvatar).filter(([_, value]) => value !== undefined)
      );
      
      await setDoc(avatarRef, cleanedAvatar);
      
      // Return the created avatar
      return {
        id: avatarRef.id,
        ...newAvatar
      } as UserAvatar;
    }
  },

  // User Inventory Management

  // Get user's inventory
  async getUserInventory(userId: string, teamId: string): Promise<UserInventory | null> {
    // On iOS, use REST API workaround
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const inventoryDoc = await firestoreRestApi.getDocument('userInventories', `${teamId}_${userId}`);
        if (!inventoryDoc) return null;
        
        const docData = inventoryDoc.data();
        return {
          id: inventoryDoc.id,
          ...docData,
          createdAt: docData.createdAt || Timestamp.now(),
          updatedAt: docData.updatedAt || Timestamp.now()
        } as UserInventory;
      } catch (error) {
        console.warn('Failed to load user inventory via REST API:', error);
        return null;
      }
    }
    
    // On web/Android, use SDK with REST fallback for permission issues
    try {
      const inventoryRef = doc(db, 'userInventories', `${teamId}_${userId}`);
      const inventoryDoc = await getDoc(inventoryRef);
      
      if (!inventoryDoc.exists()) {
        return null;
      }
      
      const docData = inventoryDoc.data();
      return {
        id: inventoryDoc.id,
        ...docData,
        createdAt: docData.createdAt,
        updatedAt: docData.updatedAt
      } as UserInventory;
    } catch (sdkError: any) {
      if (sdkError?.code === 'permission-denied' || sdkError?.message?.includes('permission')) {
        try {
          console.warn('Permission denied via SDK; retrying getUserInventory via REST API');
          const inventoryDoc = await firestoreRestApi.getDocument('userInventories', `${teamId}_${userId}`);
          if (!inventoryDoc) return null;
          
          const docData = inventoryDoc.data();
          return {
            id: inventoryDoc.id,
            ...docData,
            createdAt: docData.createdAt || Timestamp.now(),
            updatedAt: docData.updatedAt || Timestamp.now()
          } as UserInventory;
        } catch (restError) {
          console.warn('Failed to load user inventory via REST fallback:', restError);
          return null;
        }
      }
      throw sdkError;
    }
  },


  // Equip an avatar item
  async equipAvatarItem(
    userId: string,
    teamId: string,
    itemId: string,
    category: 'hat' | 'shirt' | 'pants' | 'shoes' | 'accessory' | 'background'
  ): Promise<{ success: boolean; message: string }> {
    try {
      // Check if user owns the item
      const inventory = await this.getUserInventory(userId, teamId);
      if (!inventory || !inventory.items.includes(itemId)) {
        return { success: false, message: 'You do not own this item' };
      }
      
      // Get the item to verify category
      const itemRef = doc(db, 'avatarItems', itemId);
      const itemDoc = await getDoc(itemRef);
      
      if (!itemDoc.exists()) {
        return { success: false, message: 'Item not found' };
      }
      
      const item = itemDoc.data() as AvatarItem;
      if (item.category !== category) {
        return { success: false, message: 'Item category mismatch' };
      }
      
      // Update avatar with equipped item
      await this.saveUserAvatar(userId, teamId, { [category]: itemId });
      
      return { success: true, message: 'Item equipped successfully!' };
    } catch (error: any) {
      console.error('Error equipping avatar item:', error);
      return { success: false, message: 'Failed to equip item. Please try again.' };
    }
  },

  // Personal Records Management

  // Get user's personal records
  async getUserPRs(userId: string, teamId: string): Promise<PersonalRecord[]> {
    try {
      // On iOS, use REST API workaround
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments(
          'personalRecords',
          [
            { field: 'userId', op: '==', value: userId },
            { field: 'teamId', op: '==', value: teamId }
          ]
        );
        
        const prs = documents.map(doc => {
          const docData = doc.data();
          return {
            id: doc.id,
            ...docData,
            date: docData.date || Timestamp.now(),
            createdAt: docData.createdAt || Timestamp.now(),
            updatedAt: docData.updatedAt || Timestamp.now()
          } as PersonalRecord;
        });
        
        return prs.sort((a, b) => {
          const aTime = a.date?.seconds || 0;
          const bTime = b.date?.seconds || 0;
          return bTime - aTime; // Descending order
        });
      }
      
      // On web/Android, use SDK
      // Simple query without orderBy to avoid composite index requirement
      const q = query(
        collection(db, 'personalRecords'),
        where('userId', '==', userId),
        where('teamId', '==', teamId)
      );
      
      const querySnapshot = await getDocs(q);
      
      // Sort in JavaScript instead of Firestore
      const prs = querySnapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
          date: docData.date,
          createdAt: docData.createdAt,
          updatedAt: docData.updatedAt
        } as PersonalRecord;
      });
      
      return prs.sort((a, b) => {
        const aTime = a.date?.seconds || 0;
        const bTime = b.date?.seconds || 0;
        return bTime - aTime; // Descending order
      });
    } catch (error) {
      console.warn('Failed to load user PRs:', error);
      return []; // Return empty array if collection doesn't exist or permission denied
    }
  },

  // Get all personal records for a team
  async getTeamPRs(teamId: string): Promise<PersonalRecord[]> {
    try {
      // On iOS, Firestore SDK can fail because auth.currentUser isn't set.
      // Race Times leaderboard depends on team PRs, so use REST for iOS to match web behavior.
      if (Capacitor.getPlatform() === 'ios') {
        const documents = await firestoreRestApi.queryDocuments('personalRecords', [
          { field: 'teamId', op: '==', value: teamId },
        ]);

        const prs = documents.map((doc) => {
          const docData = doc.data();
          return {
            id: doc.id,
            ...docData,
            date: docData.date,
            createdAt: docData.createdAt,
            updatedAt: docData.updatedAt,
          } as PersonalRecord;
        });

        return prs;
      }

      const q = query(
        collection(db, 'personalRecords'),
        where('teamId', '==', teamId)
      );
      
      const querySnapshot = await getDocs(q);
      
      const prs = querySnapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
          date: docData.date,
          createdAt: docData.createdAt,
          updatedAt: docData.updatedAt
        } as PersonalRecord;
      });
      
      return prs;
    } catch (error) {
      // If SDK permission/rules evaluation fails, retry via REST so the UI still loads.
      try {
        console.warn('Failed to load team PRs via SDK; retrying via REST:', error);
        const documents = await firestoreRestApi.queryDocuments('personalRecords', [
          { field: 'teamId', op: '==', value: teamId },
        ]);

        return documents.map((doc) => {
          const docData = doc.data();
          return {
            id: doc.id,
            ...docData,
            date: docData.date,
            createdAt: docData.createdAt,
            updatedAt: docData.updatedAt,
          } as PersonalRecord;
        });
      } catch (restError) {
        console.warn('Failed to load team PRs via REST fallback:', restError);
        return [];
      }
    }
  },

  // Purchase an avatar item with wallet points
  async purchaseAvatarItem(
    userId: string, 
    teamId: string, 
    itemId: string, 
    walletPoints: number
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Get all required data before transaction
      const itemRef = doc(db, 'avatarItems', itemId);
      const inventoryRef = doc(db, 'userInventories', `${teamId}_${userId}`);
      const pointsRef = doc(db, 'points', `${teamId}_${userId}`);
      
      // Read all documents first
      const [itemDoc, inventoryDoc, pointsDoc] = await Promise.all([
        getDoc(itemRef),
        getDoc(inventoryRef),
        getDoc(pointsRef)
      ]);
      
      if (!itemDoc.exists()) {
        return { success: false, error: 'Item not found' };
      }
      
      const item = itemDoc.data() as AvatarItem;
      
      // Check if item is active and belongs to the team
      if (!item.isActive || item.teamId !== teamId) {
        return { success: false, error: 'Item not available' };
      }
      
      // Check if user has enough wallet points
      if (walletPoints < item.price) {
        return { success: false, error: 'Insufficient wallet points' };
      }
      
      // Check if user already owns this item
      if (inventoryDoc.exists()) {
        const inventory = inventoryDoc.data() as UserInventory;
        if (inventory.items.includes(itemId)) {
          return { success: false, error: 'You already own this item' };
        }
      }
      
      // Check points document exists
      if (!pointsDoc.exists()) {
        return { success: false, error: 'Points document not found' };
      }
      
      const currentPoints = pointsDoc.data();
      const newWalletPoints = (currentPoints.walletPoints || 0) - item.price;
      
      if (newWalletPoints < 0) {
        return { success: false, error: 'Insufficient wallet points' };
      }
      
      // Execute the transaction with only writes
      await runTransaction(db, async (transaction) => {
        // Update inventory
        if (inventoryDoc.exists()) {
          const inventory = inventoryDoc.data() as UserInventory;
          const updatedItems = [...inventory.items, itemId];
          transaction.update(inventoryRef, {
            items: updatedItems,
            updatedAt: Timestamp.now()
          });
        } else {
          // Create new inventory
          transaction.set(inventoryRef, {
            userId,
            teamId,
            items: [itemId],
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now()
          });
        }
        
        // Update points
        transaction.update(pointsRef, {
          walletPoints: newWalletPoints,
          lastUpdated: Timestamp.now()
        });
      });
      
      // If transaction succeeded and item has ReadyPlayerMe integration, unlock the asset
      if (item.rpmAssetId) {
        try {
          const unlockResult = await ReadyPlayerMeAssetService.unlockAsset(item.rpmAssetId, userId);
          if (!unlockResult.success) {
            console.warn('Failed to unlock RPM asset, but purchase completed:', unlockResult.error);
            // Don't fail the purchase, just log the warning
          }
        } catch (error) {
          console.warn('RPM unlock failed, but purchase completed:', error);
          // Don't fail the purchase, just log the warning
        }
      }
      
      return { success: true };
    } catch (error) {
      console.error('Failed to purchase avatar item:', error);
      return { success: false, error: 'Purchase failed. Please try again.' };
    }
  },

  // Get shop items with ownership status for a user
  async getShopItemsWithOwnership(userId: string, teamId: string): Promise<(AvatarItem & { owned: boolean })[]> {
    try {
      // Get all available items for the team
      const items = await this.getAvailableItems(teamId);
      
      // Get user's inventory
      const inventory = await this.getUserInventory(userId, teamId);
      const ownedItemIds = inventory?.items || [];
      
      // Add ownership status to items
      return items.map(item => ({
        ...item,
        owned: ownedItemIds.includes(item.id)
      }));
    } catch (error) {
      console.error('Failed to get shop items with ownership:', error);
      return [];
    }
  },

  // Get all personal records for a user (across all teams)
  async getUserPersonalRecords(userId: string, teamId?: string): Promise<PersonalRecord[]> {
    try {
      // If teamId is provided, use team-based query to avoid potential permission issues
      if (teamId) {
        console.log(`getUserPersonalRecords: Using team-based query for user ${userId} in team ${teamId}`);
        const q = query(
          collection(db, 'personalRecords'),
          where('teamId', '==', teamId)
        );
        
        const querySnapshot = await getDocs(q);
        
        const prs = querySnapshot.docs
          .map(doc => {
            const docData = doc.data();
            return {
              id: doc.id,
              ...docData,
              date: docData.date,
              createdAt: docData.createdAt,
              updatedAt: docData.updatedAt
            } as PersonalRecord;
          })
          .filter(pr => pr.userId === userId); // Filter by userId after fetching
        
        console.log(`getUserPersonalRecords: Found ${prs.length} PRs for user ${userId}`);
        return prs.sort((a, b) => {
          const aTime = a.date?.seconds || 0;
          const bTime = b.date?.seconds || 0;
          return bTime - aTime; // Descending order (most recent first)
        });
      }
      
      // Otherwise, use the direct query (works for regular coaches and athletes)
      const q = query(
        collection(db, 'personalRecords'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      
      const prs = querySnapshot.docs.map(doc => {
        const docData = doc.data();
        return {
          id: doc.id,
          ...docData,
          date: docData.date,
          createdAt: docData.createdAt,
          updatedAt: docData.updatedAt
        } as PersonalRecord;
      });
      
      return prs.sort((a, b) => {
        const aTime = a.date?.seconds || 0;
        const bTime = b.date?.seconds || 0;
        return bTime - aTime; // Descending order (most recent first)
      });
    } catch (error) {
      console.warn('Failed to load user personal records:', error);
      return [];
    }
  },

  // Add a new personal record
  async addPersonalRecord(
    userId: string,
    teamId: string,
    prData: Omit<PersonalRecord, 'id' | 'userId' | 'teamId' | 'createdAt' | 'updatedAt'>
  ): Promise<string> {
    const newPR: Omit<PersonalRecord, 'id'> = {
      ...prData,
      userId,
      teamId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // iOS already uses REST-first patterns elsewhere; keep behavior consistent.
    if (Capacitor.getPlatform() === 'ios') {
      return await firestoreRestApi.createDocument('personalRecords', newPR);
    }

    // Web/Android: try SDK first, then REST fallback on permission issues.
    try {
      const prCollection = collection(db, 'personalRecords');
      const docRef = await addDoc(prCollection, newPR);
      return docRef.id;
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('Permission denied via SDK; retrying addPersonalRecord via REST API');
        return await firestoreRestApi.createDocument('personalRecords', newPR);
      }
      throw error;
    }
  },

  // Update a personal record
  async updatePersonalRecord(prId: string, updates: Partial<PersonalRecord>): Promise<void> {
    // Filter out undefined values to prevent Firestore errors
    const cleanUpdates: any = {
      updatedAt: Timestamp.now()
    };
    
    // Only include fields that have defined values
    if (updates.eventType !== undefined) cleanUpdates.eventType = updates.eventType;
    if (updates.time !== undefined) cleanUpdates.time = normalizeMinutesForStorage(updates.time);
    if (updates.date !== undefined) cleanUpdates.date = updates.date;
    if (updates.location !== undefined && updates.location !== null) cleanUpdates.location = updates.location;
    if (updates.notes !== undefined && updates.notes !== null) cleanUpdates.notes = updates.notes;
    if (updates.isVerified !== undefined) cleanUpdates.isVerified = updates.isVerified;
    if (updates.distance !== undefined) cleanUpdates.distance = updates.distance;
    
    if (Capacitor.getPlatform() === 'ios') {
      await firestoreRestApi.setDocument('personalRecords', prId, cleanUpdates, true);
      return;
    }

    try {
      const prRef = doc(db, 'personalRecords', prId);
      await updateDoc(prRef, cleanUpdates);
    } catch (error: any) {
      if (error?.code === 'permission-denied' || error?.message?.includes('permission')) {
        console.warn('Permission denied via SDK; retrying updatePersonalRecord via REST API');
        await firestoreRestApi.setDocument('personalRecords', prId, cleanUpdates, true);
        return;
      }
      throw error;
    }
  },

  // Delete a personal record
  async deletePersonalRecord(prId: string): Promise<void> {
    const prRef = doc(db, 'personalRecords', prId);
    await deleteDoc(prRef);
  },

  // Get athlete's existing PR for a specific event
  async getAthleteExistingPRForEvent(userId: string, eventType: string): Promise<PersonalRecord | null> {
    try {
      const q = query(
        collection(db, 'personalRecords'),
        where('userId', '==', userId),
        where('eventType', '==', eventType)
      );
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) return null;
      
      // If multiple PRs for same event, return the fastest
      const prs = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as PersonalRecord));
      
      return prs.reduce((fastest, current) => 
        current.time < fastest.time ? current : fastest
      );
    } catch (error) {
      console.error('Error getting existing PR for event:', error);
      return null;
    }
  },

  // Check if new time is faster than existing PR
  isTimeFaster(newTimeMinutes: number, existingTimeMinutes: number): boolean {
    return newTimeMinutes < existingTimeMinutes;
  },

  formatTimeForDisplay(minutes: number): string {
    return formatPRTimeDisplay(minutes);
  },

  // Smart upsert - create or update PR if new time is faster
  async upsertPersonalRecord(
    userId: string,
    teamId: string,
    prData: Omit<PersonalRecord, 'id' | 'userId' | 'teamId' | 'createdAt' | 'updatedAt'>
  ): Promise<{ action: 'created' | 'updated' | 'skipped'; prId?: string; message: string }> {
    try {
      console.log('upsertPersonalRecord: Starting for userId:', userId, 'eventType:', prData.eventType, 'time:', prData.time);
      
      // Check for existing PR for this event
      const existingPR = await this.getAthleteExistingPRForEvent(userId, prData.eventType);
      console.log('upsertPersonalRecord: Existing PR found:', existingPR ? 'Yes' : 'No', existingPR ? `(time: ${existingPR.time} min)` : '');
      
      if (!existingPR) {
        // No existing PR, create new one (coach/bulk entries are verified)
        console.log('upsertPersonalRecord: Creating new PR for userId:', userId);
        const prId = await this.addPersonalRecord(userId, teamId, {
          ...prData,
          isVerified: prData.isVerified !== undefined ? prData.isVerified : true,
        });
        console.log('upsertPersonalRecord: New PR created with ID:', prId);
        return { 
          action: 'created', 
          prId, 
          message: 'New PR created' 
        };
      }
      
      // Check if new time is faster
      const isFaster = this.isTimeFaster(prData.time, existingPR.time);
      console.log('upsertPersonalRecord: Time comparison - new:', prData.time, 'existing:', existingPR.time, 'isFaster:', isFaster);
      
      if (isFaster) {
        // New time is faster, update existing PR (bulk/coach entry always records as verified)
        console.log('upsertPersonalRecord: Updating existing PR:', existingPR.id);
        await this.updatePersonalRecord(existingPR.id, {
          ...prData,
          isVerified: prData.isVerified !== undefined ? prData.isVerified : true,
        });
        console.log('upsertPersonalRecord: PR updated successfully');
        return { 
          action: 'updated', 
          prId: existingPR.id, 
          message: `PR updated (previous: ${this.formatTimeForDisplay(existingPR.time)})` 
        };
      }
      
      // New time is not faster, skip
      console.log('upsertPersonalRecord: Time not faster, skipping');
      return { 
        action: 'skipped', 
        message: `Time not faster than existing PR (${this.formatTimeForDisplay(existingPR.time)})` 
      };
    } catch (error: any) {
      console.error('upsertPersonalRecord: Error for userId:', userId, 'eventType:', prData.eventType, 'error:', error);
      console.error('upsertPersonalRecord: Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  },

  // Bulk create or update PRs with smart comparison
  async bulkUpsertPRs(
    prEntries: Array<{
      userId: string;
      teamId: string;
      prData: Omit<PersonalRecord, 'id' | 'userId' | 'teamId' | 'createdAt' | 'updatedAt'>;
    }>
  ): Promise<{
    created: number;
    updated: number;
    skipped: number;
    results: Array<{ userId: string; action: string; message: string }>;
  }> {
    let created = 0;
    let updated = 0;
    let skipped = 0;
    const results: Array<{ userId: string; action: string; message: string }> = [];

    console.log('bulkUpsertPRs: Processing', prEntries.length, 'PR entries');

    for (const entry of prEntries) {
      try {
        console.log('bulkUpsertPRs: Processing entry for userId:', entry.userId, 'eventType:', entry.prData.eventType, 'time:', entry.prData.time);
        
        const result = await this.upsertPersonalRecord(
          entry.userId,
          entry.teamId,
          entry.prData
        );
        
        console.log('bulkUpsertPRs: Result for userId:', entry.userId, 'action:', result.action, 'message:', result.message);
        
        results.push({
          userId: entry.userId,
          action: result.action,
          message: result.message
        });
        
        if (result.action === 'created') created++;
        else if (result.action === 'updated') updated++;
        else if (result.action === 'skipped') skipped++;
      } catch (error: any) {
        console.error('bulkUpsertPRs: Error processing entry for userId:', entry.userId, 'error:', error);
        console.error('bulkUpsertPRs: Error details:', {
          message: error.message,
          code: error.code,
          stack: error.stack
        });
        
        results.push({
          userId: entry.userId,
          action: 'error',
          message: `Failed to save PR: ${error.message || 'Unknown error'}`
        });
      }
    }

    console.log('bulkUpsertPRs: Final results:', { created, updated, skipped, totalResults: results.length });
    return { created, updated, skipped, results };
  }
};
